import { morph } from '@theme/morph';
import { Component } from '@theme/component';
import { ThemeEvents } from '@theme/events';
import { DialogComponent, DialogCloseEvent } from '@theme/dialog';
import { mediaQueryLarge, isMobileBreakpoint, getIOSVersion } from '@theme/utilities';

export class QuickAddComponent extends Component {
  #abortController = null;
  #cachedContent = new Map();
  #cachedDescription = new Map();

  get productPageUrl() {
    const productCard = this.closest('product-card');
    const link = productCard?.getProductCardLink();
    if (!link?.href) return '';

    const url = new URL(link.href);
    const variantId = productCard?.getSelectedVariantId();
    if (variantId) url.searchParams.set('variant', variantId);
    return url.toString();
  }

  connectedCallback() {
    super.connectedCallback();
    mediaQueryLarge.addEventListener('change', this.#closeQuickAddModal);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    mediaQueryLarge.removeEventListener('change', this.#closeQuickAddModal);
  }

  handleClick = async (event) => {
    event.preventDefault();

    const url = this.productPageUrl;
    let productGrid = this.#cachedContent.get(url);
    let descriptionHtml = this.#cachedDescription.get(url);

    if (!productGrid || !descriptionHtml) {
      const html = await this.fetchProductPage(url);
      if (!html) return;

      const grid = html.querySelector('[data-product-grid-content]');
      const description =
        html.querySelector('[data-product-description]') ||
        html.querySelector('.product__description') ||
        html.querySelector('#ProductInfo .rte');

      if (grid) {
        productGrid = grid.cloneNode(true);
        this.#cachedContent.set(url, productGrid);
      }

      if (description) {
        descriptionHtml = description.innerHTML;
        this.#cachedDescription.set(url, descriptionHtml);
      }
    }

    if (productGrid) {
      const fresh = productGrid.cloneNode(true);
      this.updateQuickAddModal(fresh, descriptionHtml);
      this.#openQuickAddModal();
    }
  };

  async fetchProductPage(url) {
    this.#abortController?.abort();
    this.#abortController = new AbortController();

    try {
      const res = await fetch(url, { signal: this.#abortController.signal });
      if (!res.ok) return null;
      return new DOMParser().parseFromString(await res.text(), 'text/html');
    } catch (e) {
      if (e.name === 'AbortError') return null;
      throw e;
    }
  }

  updateQuickAddModal(productGrid, descriptionHtml) {
    const modalContent = document.getElementById('quick-add-modal-content');
    if (!modalContent) return;

    /* ---------- MOBILE LAYOUT ---------- */
    if (isMobileBreakpoint()) {
      const details = productGrid.querySelector('.product-details');
      const form = productGrid.querySelector('product-form-component');
      const price = productGrid.querySelector('product-price');
      const picker = productGrid.querySelector('variant-picker');

      const title = document.createElement('a');
      title.textContent = this.dataset.productTitle || '';
      title.href = this.productPageUrl;

      const header = document.createElement('div');
      header.className = 'product-header';
      header.appendChild(title);
      if (price) header.appendChild(price);

      productGrid.appendChild(header);
      if (picker) productGrid.appendChild(picker);
      if (form) productGrid.appendChild(form);

      details?.remove();
    }

    /* ---------- DIRECT DESCRIPTION ---------- */
    if (descriptionHtml) {
      const descriptionEl = document.createElement('div');
      descriptionEl.className = 'quick-add-description';
      descriptionEl.innerHTML = descriptionHtml;
      productGrid.appendChild(descriptionEl);
    }

    morph(modalContent, productGrid);
  }

  #openQuickAddModal() {
    document.getElementById('quick-add-dialog')?.showDialog();
  }

  #closeQuickAddModal = () => {
    document.getElementById('quick-add-dialog')?.closeDialog();
  };
}

customElements.define('quick-add-component', QuickAddComponent);

class QuickAddDialog extends DialogComponent {
  connectedCallback() {
    super.connectedCallback();
    this.addEventListener(DialogCloseEvent.eventName, this.#handleClose);
  }

  #handleClose = () => {
    const ios = getIOSVersion();
    if (!ios || ios.major >= 17 || ios.minor >= 4) return;

    requestAnimationFrame(() => {
      const grid = document.querySelector('#ResultsList [product-grid-view]');
      if (!grid) return;
      grid.style.width = `${grid.offsetWidth - 1}px`;
      requestAnimationFrame(() => (grid.style.width = ''));
    });
  };
}

customElements.define('quick-add-dialog', QuickAddDialog);
